/**
 * メガメニュー管理クラス
 * ヘッダーのメガメニューの開閉、ホバー、キーボード操作、アクセシビリティを管理します
 * @constructor
 * @param {Object} options - 設定オプション
 * @param {number} options.desktopMinWidth - デスクトップ判定の最小幅（デフォルト: 769）
 * @param {number} options.closeDelay - ホバー離脱時の閉じる遅延時間（ミリ秒、デフォルト: 200）
 */
class MegaMenuManager {
  constructor(options = {}) {
    this.options = {
      desktopMinWidth: 769,
      closeDelay: 200,
      ...options,
    };

    this.triggers = Array.from(document.querySelectorAll('[data-mega-trigger]'));
    this.header = document.querySelector('.l-header');

    this.currentTrigger = null;
    this.closeTimers = new Map();
    this.trapInitializedMenus = new WeakSet();
    this.isHoverOpen = false;

    this.init();
  }

  /**
   * 初期化処理
   * イベントリスナーの設定とARIA属性の初期化を行います
   */
  init() {
    if (this.triggers.length === 0) return;

    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');

      const menu = this.getMenu(trigger);
      if (menu) menu.hidden = true;

      trigger.addEventListener('click', (e) => {
        // Ctrl/Cmd + クリック（リンク新規タブ）は anchor でしか起きないが
        // 念のためチェックしておく。
        if (e.ctrlKey || e.metaKey) return;

        e.preventDefault();
        this.isHoverOpen = false;

        // デスクトップ／モバイル問わずトグル動作
        this.toggle(trigger);
      });

      trigger.addEventListener('keydown', (e) => {
        this.handleKeyboard(e, trigger);
      });

      this.setupHover(trigger);
    });

    // 外側クリックで閉じる
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-mega-menu]')) return;
      if (e.target.closest('.l-header__nav-item-wrapper')) return;
      this.closeAll();
    });

    // Escapeキーで閉じる
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;

      const lastTrigger = this.currentTrigger;
      this.closeAll();
      lastTrigger?.focus();
    });

    // リサイズ時の処理
    window.addEventListener('resize', this.debounce(() => {
      this.triggers.forEach((trigger) => {
        this.teardownHover(trigger);
        this.setupHover(trigger);
      });

        // 再計算（クラスの付け外しで対応）
      if (this.currentTrigger && this.getMenu(this.currentTrigger)) {
        const menu = this.getMenu(this.currentTrigger);
        if (this.isDesktop()) {
          menu.classList.add('is-attached');
          this.positionMenu(this.currentTrigger, menu);
        } else {
          menu.classList.remove('is-attached');
        }
      }
    }, 150));

    // スクロール時にヘッダー高さが変化する（is-scrolled など）場合の再配置
    window.addEventListener('scroll', this.debounce(() => {
      if (this.currentTrigger && this.getMenu(this.currentTrigger) && this.isDesktop()) {
        this.positionMenu(this.currentTrigger, this.getMenu(this.currentTrigger));
      }
    }, 50));
  }

  /**
   * メニューの開閉をトグルします
   * @param {HTMLElement} trigger - トリガー要素
   */
  toggle(trigger) {
    if (this.isOpen(trigger)) {
      this.close(trigger);
    } else {
      this.open(trigger, { reason: 'toggle' });
    }
  }

  /**
   * メニューを開きます
   * @param {HTMLElement} trigger - トリガー要素
   * @param {Object} options - オプション
   * @param {string} options.reason - 開いた理由（toggle/hover/keyboard）
   */
  open(trigger, { reason = 'toggle' } = {}) {
    const menu = this.getMenu(trigger);
    if (!menu) return;

    this.cancelClose(trigger);

    if (this.currentTrigger && this.currentTrigger !== trigger) {
      this.close(this.currentTrigger);
    }

    trigger.setAttribute('aria-expanded', 'true');
    trigger.classList.add('is-open');
    menu.hidden = false;

    this.currentTrigger = trigger;

    // Desktop: use CSS (.is-attached) and JS for precise positioning under the header
    if (this.isDesktop()) {
      menu.classList.add('is-attached');
      // compute position so menu's top === header bottom
      this.positionMenu(trigger, menu);
    } else {
      menu.classList.remove('is-attached');
    }

    this.isHoverOpen = reason === 'hover';
    this.header?.classList.toggle('is-mega-hover-open', this.isHoverOpen);

    this.setupFocusTrapOnce(menu);
  }

  /**
   * メニューを閉じます
   * @param {HTMLElement} trigger - トリガー要素
   */
  close(trigger) {
    const menu = this.getMenu(trigger);
    if (!menu) return;

    trigger.setAttribute('aria-expanded', 'false');
    trigger.classList.remove('is-open');
    menu.hidden = true;

    // remove attached marker (CSS will revert visual positioning)
    menu.classList.remove('is-attached');

    // clear inline positioning (if set by positionMenu)
    menu.style.position = '';
    menu.style.top = '';
    menu.style.left = '';
    menu.style.transform = '';

    if (this.currentTrigger === trigger) {
      this.currentTrigger = null;
    }

    this.isHoverOpen = false;
    this.header?.classList.remove('is-mega-hover-open');
  }

  /**
   * すべてのメニューを閉じます
   */
  closeAll() {
    this.closeTimers.forEach((t) => clearTimeout(t));
    this.closeTimers.clear();

    this.triggers.forEach((trigger) => this.close(trigger));
  }

  /**
   * デスクトップサイズかどうかを判定します
   * @returns {boolean} デスクトップサイズの場合true
   */
  isDesktop() {
    return window.innerWidth >= this.options.desktopMinWidth;
  }

  /**
   * ホバーイベントを設定します（デスクトップのみ）
   * @param {HTMLElement} trigger - トリガー要素
   */
  setupHover(trigger) {
    if (!this.isDesktop()) return;

    const wrapper = trigger.closest('.l-header__nav-item-wrapper');
    const menu = this.getMenu(trigger);
    if (!wrapper || !menu) return;

    wrapper.dataset.megaHoverBound = 'true';

    const onEnter = () => {
      this.cancelClose(trigger);
      this.open(trigger, { reason: 'hover' });
    };

    const onLeave = () => {
      this.scheduleClose(trigger);
    };

    wrapper.addEventListener('mouseenter', onEnter);
    wrapper.addEventListener('mouseleave', onLeave);

    // メニュー自体にも同じリスナーをつけると、
    // トリガーからメニューへ移動する際に閉じるのを防げる
    menu.addEventListener('mouseenter', onEnter);
    menu.addEventListener('mouseleave', onLeave);

    wrapper._megaEnter = onEnter;
    wrapper._megaLeave = onLeave;
    menu._megaEnter = onEnter;
    menu._megaLeave = onLeave;
  }

  /**
   * ホバーイベントを削除します
   * @param {HTMLElement} trigger - トリガー要素
   */
  teardownHover(trigger) {
    const wrapper = trigger.closest('.l-header__nav-item-wrapper');
    if (!wrapper || wrapper.dataset.megaHoverBound !== 'true') return;

    const menu = this.getMenu(trigger);

    wrapper.removeEventListener('mouseenter', wrapper._megaEnter);
    wrapper.removeEventListener('mouseleave', wrapper._megaLeave);

    if (menu) {
      menu.removeEventListener('mouseenter', menu._megaEnter);
      menu.removeEventListener('mouseleave', menu._megaLeave);
      delete menu._megaEnter;
      delete menu._megaLeave;
    }

    delete wrapper._megaEnter;
    delete wrapper._megaLeave;
    delete wrapper.dataset.megaHoverBound;
  }

  /**
   * ヘッダーの下端にピッタリ配置する（JS による微調整）
   * - position: fixed を適用し、header の bottom を top に設定する
   * - トリガーの中央に水平位置を合わせる
   */
  positionMenu(trigger, menu) {
    if (!trigger || !menu || !this.header) return;
    if (!this.isDesktop()) return;

    const headerRect = this.header.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    // top = header bottom (flush)
    const top = Math.round(headerRect.bottom);
    const left = Math.round(triggerRect.left + triggerRect.width / 2);

    menu.style.position = 'fixed';
    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
    menu.style.transform = 'translateX(-50%)';
  }



  /**
   * 遅延してメニューを閉じるようスケジュールします
   * @param {HTMLElement} trigger - トリガー要素
   */
  scheduleClose(trigger) {
    this.cancelClose(trigger);

    const timer = setTimeout(() => {
      if (this.currentTrigger === trigger) {
        this.close(trigger);
      }
    }, this.options.closeDelay);

    this.closeTimers.set(trigger, timer);
  }

  /**
   * スケジュールされたクローズをキャンセルします
   * @param {HTMLElement} trigger - トリガー要素
   */
  cancelClose(trigger) {
    const timer = this.closeTimers.get(trigger);
    if (timer) {
      clearTimeout(timer);
      this.closeTimers.delete(trigger);
    }
  }

  /**
   * キーボードイベントを処理します
   * @param {KeyboardEvent} e - キーボードイベント
   * @param {HTMLElement} trigger - トリガー要素
   */
  handleKeyboard(e, trigger) {
    const menu = this.getMenu(trigger);

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.isHoverOpen = false;
        this.header?.classList.remove('is-mega-hover-open');
        this.toggle(trigger);
        break;

      case 'ArrowDown':
        e.preventDefault();
        this.open(trigger, { reason: 'keyboard' });
        this.focusFirstFocusable(menu);
        break;

      case 'ArrowUp':
        e.preventDefault();
        this.close(trigger);
        trigger.focus();
        break;
    }
  }

  /**
   * メニュー内の最初のフォーカス可能な要素にフォーカスします
   * @param {HTMLElement} menu - メニュー要素
   */
  focusFirstFocusable(menu) {
    if (!menu) return;
    const focusable = this.getFocusable(menu);
    focusable[0]?.focus();
  }

  /**
   * フォーカストラップを設定します（1度だけ）
   * @param {HTMLElement} menu - メニュー要素
   */
  setupFocusTrapOnce(menu) {
    if (this.trapInitializedMenus.has(menu)) return;
    this.trapInitializedMenus.add(menu);

    menu.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      const focusable = this.getFocusable(menu);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  /**
   * トリガーに対応するメニュー要素を取得します
   * @param {HTMLElement} trigger - トリガー要素
   * @returns {HTMLElement|null} メニュー要素
   */
  getMenu(trigger) {
    const menuId = trigger.getAttribute('aria-controls');
    if (!menuId) return null;
    return document.getElementById(menuId);
  }

  /**
   * メニューが開いているかどうかを判定します
   * @param {HTMLElement} trigger - トリガー要素
   * @returns {boolean} 開いている場合true
   */
  isOpen(trigger) {
    return trigger.getAttribute('aria-expanded') === 'true';
  }

  /**
   * 要素内のフォーカス可能な要素を取得します
   * @param {HTMLElement} root - ルート要素
   * @returns {HTMLElement[]} フォーカス可能な要素の配列
   */
  getFocusable(root) {
    return Array.from(
      root.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.closest('[hidden]'));
  }

  /**
   * デバウンス処理を行います
   * @param {Function} fn - 実行する関数
   * @param {number} delay - 遅延時間（ミリ秒）
   * @returns {Function} デバウンスされた関数
   */
  debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }
}

export default MegaMenuManager;
